import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import type { ExpenseVatOptionDto } from '../types';
import { genericCopy } from '../utils/userFacingCopy';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
let cached: { options: ExpenseVatOptionDto[]; fetchedAt: number } | null = null;

export function useExpenseVatOptions(): {
  options: ExpenseVatOptionDto[];
  loading: boolean;
  error: string | null;
  getCurrencySymbol: (vatRate: number) => string;
  getCurrencyCode: (vatRate: number) => string;
} {
  const [options, setOptions] = useState<ExpenseVatOptionDto[]>(cached?.options ?? []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cached && Date.now() - cached.fetchedAt <= CACHE_TTL_MS) {
      queueMicrotask(() => {
        setOptions(cached!.options);
        setLoading(false);
      });
      return;
    }
    queueMicrotask(() => {
      setLoading(true);
      setError(null);
    });
    apiService
      .get<ExpenseVatOptionDto[]>(API_ENDPOINTS.REFERENCE_DATA.EXPENSE_VAT_OPTIONS)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        cached = { options: list, fetchedAt: Date.now() };
        setOptions(list);
      })
      .catch(() => {
        setError(genericCopy.vatOptions);
        setOptions([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const getCurrencySymbol = useCallback(
    (vatRate: number): string => {
      const opt = options.find((o) => o.vatRate === vatRate);
      return opt?.currencySymbol ?? '£';
    },
    [options]
  );

  const getCurrencyCode = useCallback(
    (vatRate: number): string => {
      const opt = options.find((o) => o.vatRate === vatRate);
      return opt?.currencyCode ?? 'GBP';
    },
    [options]
  );

  return { options, loading, error, getCurrencySymbol, getCurrencyCode };
}
